import i18n from "i18next";

import { lightMapStye, riskEvaluationMapStyles } from "../map/Constants";
import { LandslideNatcatRiskLegend } from "../legends/landslide/LandslideNatcatRiskLegend";
import { LandslideNatcatRiskTIVLegend } from "../legends/landslide/LandslideNatcatRiskTIVLegend";
import { SeismicClassificationITALegend } from "../legends/seismic/SeismicClassificationITALegend";
import { SeismicTIVRiskLegend } from "../legends/seismic/SeismicTIVRiskLegend";
import { SeismicNatcatLegend } from "../legends/seismic/SeismicNatcatLegend";
import { legendDetails } from "../legends/seismic/SeismicUtils";
import { seismicGreenShades } from "../util/MapUtils";
import { natcatAnalyticsLayerId, natcatAnalyticsDefaultLayer } from "./NatcatMapFillLayers";
import { getInlandFloodHowdenLayers } from "./InlandFloodLayers";
import { geojsonComuni, geojsonH6, geojsonH7 } from "./NatCatGeojsons";
import { PermissionIdEnum } from "../util/types/permissions/permissionIdEnum";

const landslideShades = ["#F7F7F7", "#feff73", "#f5cb40", "#e66800", "#e60200", "#730102"];

const natCatSeismicTivFillLayer = {
  id: natcatAnalyticsLayerId,
  type: "fill",
  paint: {
    "fill-color": [
      "step",
      ["get", "S_TIV_Z1Z2"],
      seismicGreenShades[0],
      10,
      seismicGreenShades[1],
      33,
      seismicGreenShades[2],
      100,
      seismicGreenShades[3],
      330,
      seismicGreenShades[4],
      600,
      seismicGreenShades[5],
      9999,
      seismicGreenShades[6],
    ],
    "fill-opacity": ["step", ["get", "S_TIV_Z1Z2"], 0.1, 1, 0.2, 3, 0.3, 10, 0.4, 33, 0.5, 100, 0.6],
  },
  beforeId: "road-minor",
};

const natCatSeismicHowdenFillLayer = {
  id: natcatAnalyticsLayerId,
  type: "fill",
  paint: {
    "fill-color": [
      "step",
      ["get", "HS_TIV_P5_10"],
      seismicGreenShades[0],
      3,
      seismicGreenShades[0],
      10,
      seismicGreenShades[1],
      33,
      seismicGreenShades[2],
      100,
      seismicGreenShades[3],
      330,
      seismicGreenShades[4],
      600,
      seismicGreenShades[5],
      9999,
      seismicGreenShades[6],
    ],
    "fill-opacity": ["step", ["get", "HS_TIV_P5_10"], 0.1, 1, 0.2, 3, 0.3, 10, 0.4, 33, 0.5, 100, 0.6],
  },
  beforeId: "road-minor",
};

const natCatLandslideTivFillLayer = {
  id: natcatAnalyticsLayerId,
  type: "fill",
  paint: {
    "fill-color": [
      "step",
      ["get", "LS_TIV_P3P4"],
      landslideShades[0],
      2,
      landslideShades[0],
      6,
      landslideShades[1],
      14,
      landslideShades[2],
      24,
      landslideShades[3],
      52,
      landslideShades[4],
      100,
      landslideShades[5],
    ],
    "fill-opacity": [
      "step",
      ["get", "LS_TIV_P3P4"],
      0.1,
      2,
      0.2,
      6,
      0.3,
      14,
      0.4,
      24,
      0.5,
      52,
      0.6,
      100,
      0.7,
    ],
  },
  beforeId: "road-minor",
};

const natCatLandslideTivHex7FillLayer = {
  id: natcatAnalyticsLayerId,
  type: "fill",
  paint: {
    "fill-color": [
      "step",
      ["get", "LS_TIV_P3P4"],
      landslideShades[0],
      1,
      landslideShades[0],
      3,
      landslideShades[1],
      7,
      landslideShades[2],
      11,
      landslideShades[3],
      27,
      landslideShades[4],
      85,
      landslideShades[5],
    ],
    "fill-opacity": ["step", ["get", "LS_TIV_P3P4"], 0.1, 1, 0.2, 3, 0.3, 7, 0.4, 11, 0.5, 27, 0.6, 85, 0.7],
  },
  beforeId: "road-minor",
};

export const natcatLayersEnum = () => ({
  noHAzardOrKpi: {
    id: "noHAzardOrKpi",
    label: "No hazard or kpi",
    mapLayer: lightMapStye,
    mapGeojsonLayer: natcatAnalyticsDefaultLayer,
  },
  // LANDSLIDE
  landslide: {
    id: "landslide",
    label: "Landslide Ispra Risk",
    // permissions: ["super", "CYS_RISK_LANDSLIDE_ISPRA", "CYS_RISK_LANDSLIDE_HOWDEN"],
    permissions: [PermissionIdEnum.LandslideRiskIspra],
    mapGeojsonLayer: natcatAnalyticsDefaultLayer,
    legend: <LandslideNatcatRiskLegend />,
    mapLayer: riskEvaluationMapStyles.landSlideIspraMapboxStyle,
  },
  landslideRiskTivComunes: {
    id: "landslideRiskTivComunes",
    label: "Landslide Risk TIV ",
    // permissions: ["super", "CYS_RISK_LANDSLIDE_ISPRA"],
    permissions: [PermissionIdEnum.LandslideRiskIspra],
    mapGeojsonLayer: natCatLandslideTivFillLayer,
    legend: (
      <LandslideNatcatRiskTIVLegend
        title={`${i18n.t("riskAssessment.landslideRiskIspra")} TIV`}
        indexValues={[2, 6, 14, 24, 52, 100]}
        areaUnit={"commune"}
      />
    ),
    geojsonUrl: geojsonComuni,
    geojsonProp: "LS_TIV_P3P4",
  },
  landslideRiskTivHex6: {
    id: "landslideRiskTivHex6",
    label: "Landslide Risk TIV Hex 6",
    // permissions: ["super", "CYS_RISK_LANDSLIDE_ISPRA", "CYS_RISK_LANDSLIDE_HOWDEN"],
    permissions: [PermissionIdEnum.LandslideRiskIspra],
    mapGeojsonLayer: natCatLandslideTivFillLayer,
    legend: (
      <LandslideNatcatRiskTIVLegend
        title={`${i18n.t("riskAssessment.landslideRiskIspra")} TIV Hex 6`}
        indexValues={[2, 6, 14, 24, 52, 100]}
        areaUnit={"hexagon of 36 km²"}
      />
    ),
    geojsonUrl: geojsonH6,
    geojsonProp: "LS_TIV_P3P4",
  },
  landslideRiskTivHex7: {
    id: "landslideRiskTivHex7",
    label: "Landslide Risk TIV Hex 7",
    // permissions: ["super", "CYS_RISK_LANDSLIDE_ISPRA", "CYS_RISK_LANDSLIDE_HOWDEN"],
    permissions: [PermissionIdEnum.LandslideRiskIspra],
    mapGeojsonLayer: natCatLandslideTivHex7FillLayer,
    legend: (
      <LandslideNatcatRiskTIVLegend
        title={`${i18n.t("riskAssessment.landslideRiskIspra")} TIV Hex 7`}
        indexValues={[1, 3, 7, 11, 27, 85]}
        areaUnit={"hexagon of 5 km²"}
      />
    ),
    geojsonUrl: geojsonH7,
    geojsonProp: "LS_TIV_P3P4",
  },
  // INLAND FLOOD
  ...getInlandFloodHowdenLayers(),
  // SEISMIC
  seismicRisk: {
    id: "seismicRisk",
    label: "Seismic Risk",
    // permissions: ["super", "CYS_RISK_SEISMIC_ISPRA"],
    permissions: [PermissionIdEnum.SeismicRiskIspra],
    legend: <SeismicClassificationITALegend />,
    mapLayer: riskEvaluationMapStyles.seismicClassificationITARiskLighterStyle,
    mapGeojsonLayer: natcatAnalyticsDefaultLayer,
  },
  seismicRiskHowden: {
    id: "seismicRiskHowden",
    label: "Seismic Risk Howden",
    // permissions: ["super", "CYS_RISK_SEISMIC_HOWDEN"],
    permissions: [PermissionIdEnum.SeismicRiskHowden],
    legend: <SeismicNatcatLegend />,
    mapGeojsonLayer: natcatAnalyticsDefaultLayer,
    mapLayer: riskEvaluationMapStyles.seismicNatcatRiskStyle,
  },
  seismicRiskTivComunes: {
    id: "seismicRiskTivComunes",
    label: "Seismic Risk TIV ",
    // permissions: ["super", "CYS_RISK_SEISMIC_ISPRA"],
    permissions: [PermissionIdEnum.SeismicRiskIspra],
    mapGeojsonLayer: natCatSeismicTivFillLayer, // 10535 - 14.05.2024 largest value
    legend: (
      <SeismicTIVRiskLegend
        title={`${i18n.t("legend.seismicRisk")} TIV`}
        detailsTxt={legendDetails.protezioneCivileLegend}
      />
    ),
    geojsonUrl: geojsonComuni,
    geojsonProp: "S_TIV_Z1Z2",
  },
  seismicRiskHowdenTiv: {
    id: "seismicRiskHowdenTiv",
    label: "Seismic Risk Howden TIV",
    // permissions: ["super", "CYS_RISK_SEISMIC_HOWDEN"],
    permissions: [PermissionIdEnum.SeismicRiskHowden],
    mapGeojsonLayer: natCatSeismicHowdenFillLayer, //10161- 14.05.2024 largest value
    legend: (
      <SeismicTIVRiskLegend
        title={`${i18n.t("legend.seismicHowdenRisk")} TIV`}
        detailsTxt={legendDetails.howden}
      />
    ),
    geojsonUrl: geojsonComuni,
    geojsonProp: "HS_TIV_P5_10",
  },
  seismicRiskTivHex6: {
    id: "seismicRiskTivHex6",
    label: "Seismic Risk TIV Hex 6",
    // permissions: ["super", "CYS_RISK_SEISMIC_ISPRA"],
    permissions: [PermissionIdEnum.SeismicRiskIspra],
    mapGeojsonLayer: natCatSeismicTivFillLayer, // 3637 - 14.05.2024 largest value
    legend: (
      <SeismicTIVRiskLegend
        title={`${i18n.t("legend.seismicRisk")} Hex 6`}
        detailsTxt={legendDetails.protezioneCivileLegend}
      />
    ),
    geojsonUrl: geojsonH6,
    geojsonProp: "S_TIV_Z1Z2",
  },
  seismicRiskHowdenTivHex6: {
    id: "seismicRiskHowdenTivHex6",
    label: "Seismic Risk Howden TIV Hex 6",
    // permissions: ["super", "CYS_RISK_SEISMIC_HOWDEN"],
    permissions: [PermissionIdEnum.SeismicRiskHowden],
    mapGeojsonLayer: natCatSeismicHowdenFillLayer, // 10199 - 14.05.2024 largest value
    legend: (
      <SeismicTIVRiskLegend
        title={`${i18n.t("legend.seismicHowdenRisk")} Hex 6`}
        detailsTxt={legendDetails.howdenHex6}
      />
    ),
    geojsonUrl: geojsonH6,
    geojsonProp: "HS_TIV_P5_10",
  },
  seismicRiskTivHex7: {
    id: "seismicRiskTivHex7",
    label: "Seismic Risk TIV Hex 7",
    // permissions: ["super", "CYS_RISK_SEISMIC_ISPRA"],
    permissions: [PermissionIdEnum.SeismicRiskIspra],
    mapGeojsonLayer: natCatSeismicTivFillLayer, // 2708 - 14.05.2024 largest value
    legend: (
      <SeismicTIVRiskLegend
        title={`${i18n.t("legend.seismicRisk")} Hex 7`}
        detailsTxt={legendDetails.protezioneCivileLegend}
      />
    ),
    geojsonUrl: geojsonH7,
    geojsonProp: "S_TIV_Z1Z2",
  },
  seismicRiskHowdenTivHex7: {
    id: "seismicRiskHowdenTivHex7",
    label: "Seismic Risk Howden TIV Hex 7",
    // permissions: ["super", "CYS_RISK_SEISMIC_HOWDEN"],
    permissions: [PermissionIdEnum.SeismicRiskHowden],
    mapGeojsonLayer: natCatSeismicHowdenFillLayer, //10151 - 14.05.2024 largest value
    legend: (
      <SeismicTIVRiskLegend
        title={`${i18n.t("legend.seismicHowdenRisk")} Hex 7`}
        detailsTxt={legendDetails.howdenHex7}
      />
    ),
    geojsonUrl: geojsonH7,
    geojsonProp: "HS_TIV_P5_10",
  },
});
